import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  createCreativeReport,
  deleteCreativeReport,
  deleteReportSection,
  fetchCreativeReportList,
  updateCreativeReport,
  updateReportSection,
  duplicateReportSection,
  retrySyncFailedAssets,
  duplicateCreativeReport,
  updateSectionOrders,
  getDataConfigurations,
  updateReportOrders,
  copyReportToAccounts,
} from "../apiService";
import { fetchAccounts } from "@/api/globalApiService";
import { Account } from "@/types";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { useGlobalStore } from "@/GlobalStore";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { Copy, Trash } from "lucide-react";
import {
  CreativeReport,
  CreativeReportListItem,
  CreativeReportSection,
  ReportType,
} from "../types";
import { CreativeSectionConfiguration, TimeInterval } from "@/types";
import { LetterCaseCapitalizeIcon } from "@radix-ui/react-icons";
import { getDefaultColumnChoices } from "../utils/getDefaultColumnChoices";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import SyncedAdsStatus from "@/components/common/SyncedAdsStatus";
import { getPredefinedRanges, getDefaultSevenDay, formatForAPI } from "@/utils/dateUtils";
import SidebarWrapper from "@/components/common/sidebar/SidebarWrapper";
import { IconLoader2, IconX } from "@tabler/icons-react";
import SidebarItem, { ISidebarItem } from "@/components/common/sidebar/SidebarItem";
import SidebarSubItem, {
  ISidebarSubItem,
} from "@/components/common/sidebar/SidebarSubItem";
import SidebarSeparator from "@/components/common/sidebar/SidebarSeparator";
import { SidebarLoader } from "@/components/common/sidebar/SidebarLoader";
import { Skeleton } from "@/components/ui/skeleton";
import { buildRoute } from "@/router/constants";
import {
  RiAddLargeLine,
  RiAlarmWarningLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiFolderChartLine,
  RiFolderLine,
  RiHome6Line,
  RiLogoutBoxRLine,
  RiPencilLine,
  RiSearchLine,
} from "@remixicon/react";
import { Button } from "@/components/ui/button";
import { Reorder } from "framer-motion";
import useStrictNavigation from "@/hooks/useStrictNavigation";
import { CopyToAccountsPopover } from "./CopyToAccountsPopover";
import ToastCompact from "@/components/common/toast/ToastCompact";
import SectionPreset from "./preset/SectionPreset";
import type { PresetItem } from "./preset/PresetContent";
import {
  accountDefaultDateKeyToTimeInterval,
  getAccountDefaultDateKeyFromAccount,
} from "@/utils/defaultDateUtils";

export type CreativesSidebarRef = {
  /** Re-run report selection for the current sidebar item and trigger report config fetch */
  syncReportSelection: () => void;
};

type Props = {
  reportConfig: CreativeReport | null;
  setReportConfig: (config: CreativeReport | null) => void;
  reportConfigLoading: boolean;
  selectedSection: (CreativeReportSection & { overrideScroll?: boolean }) | null;
  setSelectedSection: (
    section: (CreativeReportSection & { overrideScroll?: boolean }) | null,
  ) => void;
  handleAddSection: (
    presetConfiguration?: Partial<CreativeSectionConfiguration>,
    title?: string,
    presetItem?: PresetItem,
  ) => Promise<void>;
  isAddingSection: boolean;
  hideAddSectionButton?: boolean;
  selectedSidebarItem: ISidebarItem | null;
  setSelectedSidebarItem: (item: ISidebarItem | null) => void;
};

export const creativeSidebarItems: Record<ReportType, ISidebarItem> = {
  dashboard: {
    type: "tab" as const,
    value: "dashboard",
    label: "Overview",
    icon: RiHome6Line,
    id: "dashboard",
    route: "overview",
  },
  regular: {
    type: "tab" as const,
    value: "reports",
    label: "Reports",
    icon: RiFolderChartLine,
    id: "regular",
    route: "reports",
  },
  fatigue: {
    type: "tab" as const,
    value: "fatigue",
    label: "Fatigue",
    icon: RiAlarmWarningLine,
    id: "fatigue",
    route: "fatigue",
    beta: true,
  },
};

const CreativesSidebar = forwardRef<CreativesSidebarRef, Props>(function CreativesSidebar(
  {
    reportConfig,
    setReportConfig,
    reportConfigLoading,
    selectedSection,
    setSelectedSection,
    handleAddSection,
    isAddingSection,
    hideAddSectionButton,
    selectedSidebarItem,
    setSelectedSidebarItem,
  },
  ref,
) {
  const { reportId } = useParams();
  const navigate = useStrictNavigation();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  // Use selectors to subscribe only to needed state
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const creativeReportList = useGlobalStore((state) => state.creativeReportList);
  const setCreativeReportList = useGlobalStore((state) => state.setCreativeReportList);
  const isCreativeListLoading = useGlobalStore((state) => state.isCreativeListLoading);
  const setIsCreativeListLoading = useGlobalStore(
    (state) => state.setIsCreativeListLoading,
  );
  const selectedCreativeReport = useGlobalStore((state) => state.selectedCreativeReport);
  const setSelectedCreativeReport = useGlobalStore(
    (state) => state.setSelectedCreativeReport,
  );
  const dataConfigurations = useGlobalStore((state) => state.dataConfigurations);
  const isAccountSynced = useGlobalStore((state) => state.isAccountSynced);
  const accountSyncStatus = useGlobalStore((state) => state.accountSyncStatus);
  const setDataConfigurations = useGlobalStore((state) => state.setDataConfigurations);
  const allowedDateRange = useGlobalStore((state) => state.allowedDateRange);

  // Available group_by option keys – used to hide presets that don't match the account's config
  const availableGroupByKeys = useMemo(() => {
    const groupByDropdown = dataConfigurations?.dropdowns?.find((d) => d.key === "group_by");
    if (!groupByDropdown) return undefined;
    return new Set(groupByDropdown.options.map((o) => o.key));
  }, [dataConfigurations?.dropdowns]);

  // Refs for report polling (not for asset syncing)
  const isMounted = useRef(true);
  const currentAccountRef = useRef<string>();
  // Ref to read current sidebar item in async callbacks (avoids overwriting user's selection when fetch completes)
  const selectedSidebarItemRef = useRef<ISidebarItem | null>(selectedSidebarItem);

  const [dashboardReport, setDashboardReport] = useState<CreativeReportListItem | null>(
    null,
  );
  // Add search state only for reports
  const [isReportSearching, setIsReportSearching] = useState(false);
  const [reportSearchQuery, setReportSearchQuery] = useState("");
  const reportSearchInputRef = useRef<HTMLInputElement>(null);

  const [isCreatingReport, setIsCreatingReport] = useState(false);

  const [isRetryingSync, setIsRetryingSync] = useState(false);

  // Accounts for "Copy to accounts" popover (fetched on mount and on account change)
  const [copyToAccountsList, setCopyToAccountsList] = useState<Account[]>([]);
  const [copyToAccountsLoading, setCopyToAccountsLoading] = useState(false);

  // Add state for delete alert
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false);
  const reportToDelete = useRef<CreativeReportListItem | null>(null);

  // Add state for add-section preset dialog
  const [isAddSectionDialogOpen, setIsAddSectionDialogOpen] = useState(false);

  // Add state for polling pending reports
  const reportPollingTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    selectedSidebarItemRef.current = selectedSidebarItem;
  }, [selectedSidebarItem]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Clear polling timeout on unmount
      if (reportPollingTimeoutRef.current) {
        clearTimeout(reportPollingTimeoutRef.current);
        reportPollingTimeoutRef.current = undefined;
      }
    };
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    const clearTimeoutRef = () => {
      if (reportPollingTimeoutRef.current) {
        clearTimeout(reportPollingTimeoutRef.current);
        reportPollingTimeoutRef.current = undefined;
      }
    };

    // Update currentAccountRef when account changes
    currentAccountRef.current = selectedAccount?.id;

    // Only reset state after the first render (not on mount)
    if (!isFirstRender.current) {
      setCreativeReportList([]);
      setDashboardReport(null);
      setSelectedCreativeReport(null);

      setReportConfig(null);
      setSelectedSection(null);
      clearTimeoutRef();

      // Navigation will be handled by the unified navigation effect below
    }
    isFirstRender.current = false;

    return () => {
      clearTimeoutRef();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id]);

  const createDefaultReport = (): Omit<CreativeReportListItem, "id"> & {
    section_configuration: CreativeSectionConfiguration;
  } => {
    const timezone = selectedAccount?.time_zone ?? "UTC";
    const defaultKey = getAccountDefaultDateKeyFromAccount(selectedAccount);
    const label = accountDefaultDateKeyToTimeInterval(defaultKey);
    const presets = getPredefinedRanges(timezone);
    const presetRange = presets[label] ?? presets[TimeInterval.LAST_7_DAYS];
    const dateRangeFrom: Date = presetRange?.from ?? new Date();
    const dateRangeTo: Date = presetRange?.to ?? new Date();

    return {
      title: `Report ${new Date()
        .toLocaleString("en-GB", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/\//g, "-")}`,
      description: "",
      metadata: {
        date: {
          start_date: formatForAPI(dateRangeFrom, timezone),
          end_date: formatForAPI(dateRangeTo, timezone),
          label,
        },
      },
      account: selectedAccount?.id,
      section_configuration: {
        dropdowns: {
          group_by:
            dataConfigurations?.dropdowns?.find((d) => d.key === "group_by")?.default ??
            null,
        },
        column_choices: getDefaultColumnChoices(dataConfigurations),
        date: {
          start_date: formatForAPI(dateRangeFrom, timezone),
          end_date: formatForAPI(dateRangeTo, timezone),
          label: TimeInterval.LAST_7_DAYS,
        },
      },
    };
  };

  const handleAddReport = async () => {
    if (!selectedAccount?.id) return;
    try {
      setIsCreatingReport(true);
      const res = await createCreativeReport(selectedAccount?.id, createDefaultReport());
      setCreativeReportList([res.report, ...creativeReportList]);
      handleReportChange(res.report);
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreatingReport(false);
    }
  };

  // only navigates for normal report
  const handleReportChange = useCallback(
    (report: CreativeReportListItem | null) => {
      if (!selectedAccount?.id || selectedCreativeReport?.id === report?.id) return;
      setReportConfig(null);
      setSelectedCreativeReport(report);
      // Navigate to specific report route for regular reports only
      if (report && report.type === ReportType.Regular && report.id) {
        navigate(`${buildRoute.report(selectedAccount.id, report.id)}${location.search}`);
      }
    },
    [
      selectedAccount?.id,
      selectedCreativeReport?.id,
      setReportConfig,
      setSelectedCreativeReport,
      navigate,
      location.search,
    ],
  );

  // Main handler for sidebar item clicks - implements the predictable flow
  const handleSidebarItemClick = useCallback(
    (
      item: ISidebarItem,
      freshReportsList?: CreativeReportListItem[],
      shouldNavigate = true,
    ) => {
      if (!selectedAccount?.id) return;

      // Step 1: Set sidebar item
      setSelectedSidebarItem(item);

      // Step 2: Find the appropriate report first (if reports are loaded)
      // Use fresh reports list if provided, otherwise use state
      const reportsToUse = freshReportsList || creativeReportList;
      let selectedReport: CreativeReportListItem | null = null;
      if (reportsToUse.length > 0) {
        switch (item.id) {
          case ReportType.Dashboard: {
            selectedReport =
              reportsToUse.find(
                (r) => r.type === ReportType.Dashboard && r.sync_status === "success",
              ) || (dashboardReport?.sync_status === "success" ? dashboardReport : null);
            break;
          }
          case ReportType.Regular: {
            if (reportId) {
              selectedReport =
                reportsToUse.find(
                  (r) =>
                    r.type === ReportType.Regular &&
                    r.sync_status === "success" &&
                    r.id === reportId,
                ) || null;
            } else {
              selectedReport =
                reportsToUse.find(
                  (r) => r.type === ReportType.Regular && r.sync_status === "success",
                ) || null;
            }
            break;
          }
          case ReportType.Fatigue: {
            selectedReport =
              reportsToUse.find(
                (r) => r.type === ReportType.Fatigue && r.sync_status === "success",
              ) || null;
            break;
          }
        }
      }

      // Navigation for sidebar item selection
      if (shouldNavigate && item.route) {
        navigate(`${item.route}${location.search}`, { replace: true });
      }

      // Step 3: Set the selected report and navigation to report_id
      handleReportChange(selectedReport);
      // If reports aren't loaded yet, the useEffect will handle report selection
    },
    [
      selectedAccount?.id,
      setSelectedSidebarItem,
      reportId,
      creativeReportList,
      dashboardReport,
      handleReportChange,
      navigate,
      location.search,
    ],
  );

  useImperativeHandle(
    ref,
    () => ({
      syncReportSelection: () => {
        const item = selectedSidebarItemRef.current;
        if (!item) return;
        const list = useGlobalStore.getState().creativeReportList;
        const withDashboard =
          list.length || dashboardReport
            ? [...list, ...(dashboardReport ? [dashboardReport] : [])]
            : undefined;
        handleSidebarItemClick(item, withDashboard, false);
      },
    }),
    [handleSidebarItemClick, dashboardReport],
  );

  // Initial load effect - handles setting sidebar item based on current route
  useEffect(() => {
    if (!selectedAccount?.id || selectedCreativeReport) return;
    const currentPath = location.pathname;
    const sidebarItemFromRoute = Object.values(creativeSidebarItems).find((item) =>
      currentPath.includes(item.route || ""),
    );

    if (sidebarItemFromRoute) {
      // should navigate is false because we are selecting sidebar item from route already at correct path
      handleSidebarItemClick(sidebarItemFromRoute, undefined, false);
      return;
    }

    // Check if we're on analyze base route without child route
    if (!selectedSidebarItem) {
      handleSidebarItemClick(creativeSidebarItems[ReportType.Dashboard]);
      return;
    }

    if (selectedSidebarItem && !sidebarItemFromRoute) {
      navigate(selectedSidebarItem.route || "");
    }
  }, [
    selectedAccount?.id,
    location.pathname,
    handleSidebarItemClick,
    selectedSidebarItem,
    navigate,
    selectedCreativeReport,
  ]);

  useEffect(() => {
    if (selectedAccount?.id && isAccountSynced) {
      void fetchAndPollReports(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountSynced]);

  // Fetch accounts for "Copy to accounts" on mount and when selected account changes
  useEffect(() => {
    if (!selectedAccount?.id || !selectedAccount?.platform) {
      setCopyToAccountsList([]);
      return;
    }
    let cancelled = false;
    setCopyToAccountsLoading(true);
    fetchAccounts({
      platform: selectedAccount.platform,
      page: 1,
      search: "",
      pageSize: 1000,
    })
      .then((data) => {
        if (cancelled) return;
        const otherAccounts = data.results.filter(
          (acc) => acc.id !== selectedAccount.id && acc.role !== "viewer",
        );
        setCopyToAccountsList(otherAccounts);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.error("Error fetching accounts for copy:", error);
          setCopyToAccountsList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCopyToAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccount?.id, selectedAccount?.platform]);

  useEffect(() => {
    if (isCreativeListLoading) return;
    if (creativeReportList.length === 0 && dataConfigurations && selectedSidebarItem) {
      setSelectedCreativeReport(createDefaultReport());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creativeReportList,
    dataConfigurations,
    isCreativeListLoading,
    selectedSidebarItem,
  ]);

  // Add useEffect for search input focus
  useEffect(() => {
    if (isReportSearching && reportSearchInputRef.current) {
      reportSearchInputRef.current.focus();
    }
  }, [isReportSearching]);

  // Filter reports based on search query
  const filteredReports = useMemo(
    () =>
      creativeReportList
        .filter((report) => report.type === selectedSidebarItem?.id)
        .filter((report) => report.sync_status === "success")
        .filter((report) =>
          report.title.toLowerCase().includes(reportSearchQuery.toLowerCase()),
        ),
    [creativeReportList, reportSearchQuery, selectedSidebarItem?.id],
  );

  // Get pending reports for skeleton loaders
  const pendingReports = creativeReportList
    .filter((report) => report.type === selectedSidebarItem?.id)
    .filter((report) => report.sync_status === "pending");

  // Animation delay to allow drop animation to complete
  const DROP_ANIMATION_DELAY = 300;

  // State for managing report order with framer-motion
  const [reportsOrder, setReportsOrder] = useState<CreativeReportListItem[]>([]);
  const [isDraggingReports, setIsDraggingReports] = useState(false);
  const [draggingReportId, setDraggingReportId] = useState<string | null>(null);
  const reportsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update reports order when filteredReports changes
  useEffect(() => {
    if (!isDraggingReports) {
      setReportsOrder(filteredReports);
    }
  }, [filteredReports, isDraggingReports]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (reportsTimeoutRef.current) {
        clearTimeout(reportsTimeoutRef.current);
      }
    };
  }, []);

  // Handle reorder during drag (update local state only)
  const handleReportsReorder = (newOrder: CreativeReportListItem[]) => {
    setReportsOrder(newOrder);
  };

  // Handle drag end (commit changes to parent)
  const handleReportsDragEnd = () => {
    // Clear any existing timeout
    if (reportsTimeoutRef.current) {
      clearTimeout(reportsTimeoutRef.current);
    }

    // Delay state update to allow drop animation to complete smoothly
    reportsTimeoutRef.current = setTimeout(() => {
      setIsDraggingReports(false);
      setDraggingReportId(null);
      // Only call API if order actually changed
      const hasChanged = reportsOrder.some(
        (report, index) => report.id !== filteredReports[index]?.id,
      );
      if (hasChanged) {
        void handleReportReorder(reportsOrder);
      }
    }, DROP_ANIMATION_DELAY);
  };

  // Handle child reordering with proper API call
  const handleChildReorder = async (children: ISidebarSubItem[]) => {
    if (!selectedAccount?.id || !selectedCreativeReport?.id || !reportConfig) return;

    // Convert children back to sections
    const reorderedSections = children
      .map((child) => {
        const originalSection = reportConfig.sections.find((s) => s.id === child.id);
        return originalSection!;
      })
      .filter(Boolean);

    // Optimistically update the UI
    setReportConfig({
      ...reportConfig,
      sections: reorderedSections,
    });

    try {
      // Prepare the section orders
      const sectionOrders = reorderedSections.map((section, index) => ({
        section_id: section.id,
        order: index,
      }));

      await updateSectionOrders(
        selectedAccount?.id,
        selectedCreativeReport.id,
        sectionOrders,
      );
    } catch (error) {
      console.error("Failed to update section orders:", error);
      // Revert to original order on error
      setReportConfig({
        ...reportConfig,
        sections: reportConfig.sections,
      });
    }
  };

  const handleRetrySync = useCallback(async () => {
    if (!selectedAccount?.id) return;
    try {
      setIsRetryingSync(true);
      const response = await retrySyncFailedAssets(selectedAccount?.id);
      // If retry response indicates pending status, trigger a new sync
      if (response?.status === "pending") {
        const syncAccountAssets = useGlobalStore.getState().syncAccountAssets;
        if (syncAccountAssets) {
          void syncAccountAssets(selectedAccount);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRetryingSync(false);
    }
  }, [selectedAccount]);

  // Unified function to handle both initial fetch and recursive polling
  // This function handles the initial fetch with loading states and then recursively polls
  // until all reports have sync_status === "success"
  const fetchAndPollReports = useCallback(
    async (isInitialFetch = false) => {
      if (!selectedAccount?.id || !isMounted.current) return;

      try {
        // Set loading state only for initial fetch
        if (isInitialFetch) {
          setIsCreativeListLoading(true);
        }

        // Only fetch dashboard report if it doesn't exist or its status is not "success"
        let dashboardReportList;
        if (!dashboardReport || dashboardReport.sync_status !== "success") {
          dashboardReportList = await fetchCreativeReportList(
            selectedAccount?.id,
            ReportType.Dashboard,
          );
          setDashboardReport(dashboardReportList.reports[0] ?? null);
        }

        const data = await fetchCreativeReportList(selectedAccount?.id);

        //fetch data configurations in case of pending reports
        if (!isInitialFetch) {
          getDataConfigurations(selectedAccount?.id)
            .then((data) => {
              setDataConfigurations(data.configuration);
            })
            .catch((error) => {
              console.error(error);
            });
        }

        // Check if the account has changed during the API call
        if (currentAccountRef.current !== selectedAccount?.id || !isMounted.current)
          return;

        setCreativeReportList(data.reports);

        const freshList = [...data.reports, ...(dashboardReportList?.reports || [])];
        const currentItem = selectedSidebarItemRef.current;

        // When fetch completes, apply report selection for the *current* sidebar item
        // so that if the user switched tabs before fetch completed, we still select the right report
        if (data.reports.length && currentItem) {
          if (isInitialFetch) {
            handleSidebarItemClick(currentItem, freshList, false);
          } else {
            // Poll completed: if current tab has no selected report but now has success reports, select one
            const currentReport = useGlobalStore.getState().selectedCreativeReport;
            const needsSelection =
              !currentReport ||
              currentReport.type !== currentItem.id ||
              (currentItem.id === ReportType.Regular &&
                reportId &&
                currentReport.id !== reportId);
            if (needsSelection) {
              handleSidebarItemClick(currentItem, freshList, false);
            }
          }
        }

        // Check if there are any pending reports
        const hasPendingReports = data.reports.some(
          (report) => report.sync_status === "pending",
        );

        if (
          hasPendingReports &&
          isMounted.current &&
          currentAccountRef.current === selectedAccount?.id
        ) {
          // Start or continue polling if there are pending reports
          reportPollingTimeoutRef.current = setTimeout(
            () => void fetchAndPollReports(false),
            5000,
          );
        }
      } catch (error) {
        console.error("Error fetching/polling reports:", error);
        setCreativeReportList([]);
        setSelectedCreativeReport(null);
      } finally {
        // Clear loading state only for initial fetch
        if (isInitialFetch) {
          setTimeout(() => {
            setIsCreativeListLoading(false);
          }, 500);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAccount?.id, reportId, dashboardReport],
  );

  // Handle report reordering
  const handleReportReorder = async (reports: CreativeReportListItem[]) => {
    if (!selectedAccount?.id) return;

    // Store original order for rollback
    const originalReports = [...creativeReportList];

    // Get all reports of the current type (filtered + non-filtered)
    const allReportsOfType = creativeReportList.filter(
      (report) => report.type === selectedSidebarItem?.id,
    );

    // Get the original indices of filtered reports in the filtered list
    const originalIndices = reports
      .map((report) => allReportsOfType.findIndex((r) => r.id === report.id))
      .sort((a, b) => a - b);

    // Create a map of the new order for the filtered reports
    // Map each report to its new position within the original filtered positions
    const newOrderMap = new Map<string, number>();
    reports.forEach((report, newIndex) => {
      newOrderMap.set(report.id || "", originalIndices[newIndex]);
    });

    const reorderedReportsOfType = [...allReportsOfType];
    for (const [key, value] of newOrderMap.entries()) {
      reorderedReportsOfType[value] = allReportsOfType.find(
        (r) => r.id === key,
      ) as CreativeReportListItem;
    }

    const reportsOfOtherTypes = creativeReportList.filter(
      (report) => report.type !== selectedSidebarItem?.id,
    );

    // Combine the sorted reports of current type with reports of other types
    const finalUpdatedReports = [...reorderedReportsOfType, ...reportsOfOtherTypes];

    // Optimistically update the UI
    setCreativeReportList(finalUpdatedReports);

    try {
      // Prepare the report orders for all reports of the current type
      const reportOrders = reorderedReportsOfType.map((report, index) => ({
        report_id: report.id || "",
        order: reorderedReportsOfType.length - index - 1,
      }));

      await updateReportOrders(
        selectedAccount?.id || "",
        reportOrders,
        selectedSidebarItem?.id as ReportType,
      );
    } catch (error) {
      console.error("Failed to update report orders:", error);
      // Revert to original order on error
      setCreativeReportList(originalReports);
    }
  };

  // Modify the convertReportToSidebarItem function
  const convertReportToSidebarItem = (
    report: CreativeReportListItem,
  ): ISidebarSubItem => {
    return {
      id: report.id || null,
      type: "folder",
      value: report.id || "",
      label: report.title,
      icon: <RiFolderLine />,
      // Only include children if this is the selected report and we have the report config
      children:
        selectedCreativeReport?.id === report.id && reportConfig
          ? reportConfig.sections.map((section) => ({
              id: section.id || null,
              type: "tab" as const,
              value: section.id || "",
              label: section.title,
              icon: <span className="mt-1">{section.icon}</span>,
            }))
          : [],
    };
  };

  // Update the handleReportActions
  const handleReportActions = [
    {
      id: "rename",
      label: "Rename",
      icon: <RiPencilLine size={14} className="text-brand-primary-400" />,
      onClick: async (reportId: string, newName?: string) => {
        if (!selectedAccount?.id || !reportId || !newName?.trim()) return;

        // Store original states for rollback
        const originalReports = [...creativeReportList];
        const originalSelectedReport = selectedCreativeReport
          ? { ...selectedCreativeReport }
          : null;

        // Optimistically update UI
        const updatedReports = creativeReportList.map((r) =>
          r.id === reportId ? { ...r, title: newName } : r,
        );
        setCreativeReportList(updatedReports);

        if (selectedCreativeReport?.id === reportId) {
          setSelectedCreativeReport({ ...selectedCreativeReport, title: newName });
        }

        try {
          await updateCreativeReport(selectedAccount?.id, reportId, { title: newName });
        } catch (error) {
          // Rollback on error
          setCreativeReportList(originalReports);
          if (originalSelectedReport) {
            setSelectedCreativeReport(originalSelectedReport);
          }

          console.error(error);
        }
      },
    },
    {
      id: "duplicate",
      label: "Duplicate",
      icon: <RiFileCopyLine size={14} className="text-brand-primary-400" />,
      onClick: async (reportId: string) => {
        if (selectedAccount?.id && reportId) {
          try {
            const res = await duplicateCreativeReport(selectedAccount?.id, reportId);
            const currentIndex = creativeReportList.findIndex((r) => r.id === reportId);
            if (currentIndex === -1) return;
            const newList = [
              ...creativeReportList.slice(0, currentIndex),
              res,
              ...creativeReportList.slice(currentIndex),
            ];
            setCreativeReportList(newList);
            handleReportChange(res);
            toast.message(
              <ToastContent
                headingPrefix="Success!"
                subheadingSuffix="Creative report duplicated successfully."
              />,
            );
          } catch (error) {
            console.error(error);
          }
        }
      },
    },
    ...(copyToAccountsList.length > 0
      ? [
          {
            id: "copy_to_accounts",
            label: "Copy to accounts",
            icon: <RiLogoutBoxRLine size={14} className="text-brand-primary-400" />,
            onClick: () => {
              // This is handled by the custom render function
            },
            renderCustom: (reportId: string, onClose?: () => void) => {
              if (!selectedAccount?.id || !selectedAccount?.platform) return null;
              return (
                <CopyToAccountsPopover
                  accountId={selectedAccount.id}
                  reportId={reportId}
                  currentPlatform={selectedAccount.platform}
                  accounts={copyToAccountsList}
                  isLoadingAccounts={copyToAccountsLoading}
                  onCopy={async (accountIds: string[]) => {
                    try {
                      await copyReportToAccounts(
                        selectedAccount.id,
                        reportId,
                        accountIds,
                      );
                      // Show success toast
                      toast.custom((t) => (
                        <ToastCompact
                          message="Report copied Successfully."
                          onDismiss={() => toast.dismiss(t)}
                          iconBackgroundClassName="bg-[#03A28A14]"
                          iconProps={{ className: "text-[#03A28A]", size: 18 }}
                        />
                      ));
                      // Refresh the report list to show the new reports
                      if (selectedAccount?.id) {
                        void fetchAndPollReports(false);
                      }
                      // Close the dropdown menu
                      if (onClose) {
                        onClose();
                      }
                    } catch (error) {
                      console.error("Error copying report:", error);
                    }
                  }}
                >
                  <div className="cursor-pointer h-7 px-2 py-1 border-0 rounded-[8px] hover:bg-hover-gray aria-selected:bg-transparent flex items-center gap-[6px]">
                    <RiLogoutBoxRLine size={14} className="text-brand-primary-400" />
                    <span className="h-[14px] text-[12px] text-brand-primary-800">
                      Copy to accounts
                    </span>
                  </div>
                </CopyToAccountsPopover>
              );
            },
          },
        ]
      : []),
    {
      id: "delete",
      label: "Delete",
      icon: <RiDeleteBinLine size={14} className="text-[#FF0000]" />,
      onClick: (reportId: string) => {
        const report = creativeReportList.find((r) => r.id === reportId);
        reportToDelete.current = report || null;
        setOpenDeleteAlert(true);
      },
      className: "text-[#FF0000] hover:text-[#FF0000] bg-[#FFF7F7] hover:bg-[#FFEBEB]",
    },
  ];

  // Update the handleSectionActions
  const handleSectionActions = [
    {
      id: "rename",
      label: "Rename",
      icon: <RiPencilLine size={14} className="text-brand-primary-400" />,
      onClick: async (sectionId: string, newName?: string) => {
        // Skip actions on optimistic sections that don't have a real backend ID yet
        if (sectionId.startsWith("__temp-")) return;
        if (
          !selectedAccount?.id ||
          !selectedCreativeReport?.id ||
          !sectionId ||
          !newName?.trim()
        )
          return;

        const oldName = reportConfig?.sections.find((s) => s.id === sectionId)?.title;
        if (reportConfig) {
          const updatedSections = reportConfig.sections.map((s) =>
            s.id === sectionId ? { ...s, title: newName } : s,
          );
          setReportConfig({
            ...reportConfig,
            sections: updatedSections,
          });
        }

        if (selectedSection?.id === sectionId) {
          setSelectedSection({ ...selectedSection, title: newName });
        }
        try {
          await updateReportSection(
            selectedAccount?.id,
            selectedCreativeReport.id,
            sectionId,
            {
              title: newName,
            },
          );
        } catch (error) {
          console.error(error);
          if (reportConfig) {
            const updatedSections = reportConfig.sections.map((s) =>
              s.id === sectionId ? { ...s, title: oldName || "" } : s,
            );
            setReportConfig({
              ...reportConfig,
              sections: updatedSections,
            });
          }

          if (selectedSection?.id === sectionId) {
            setSelectedSection({ ...selectedSection, title: oldName || "" });
          }
        }
      },
    },
    {
      id: "duplicate",
      label: "Duplicate",
      icon: <RiFileCopyLine size={14} className="text-brand-primary-400" />,
      onClick: async (sectionId: string) => {
        // Skip actions on optimistic sections that don't have a real backend ID yet
        if (sectionId.startsWith("__temp-")) return;
        if (selectedAccount?.id && selectedCreativeReport?.id && sectionId) {
          try {
            const duplicatedSection = await duplicateReportSection(
              selectedAccount?.id,
              selectedCreativeReport.id,
              sectionId,
            );
            if (reportConfig) {
              setReportConfig({
                ...reportConfig,
                sections: [...reportConfig.sections, duplicatedSection],
              });
            }
            toast.message(
              <ToastContent
                headingPrefix="Success!"
                subheadingSuffix="Section duplicated successfully."
              />,
            );
          } catch (error) {
            console.error(error);
          }
        }
      },
    },
    {
      id: "delete",
      label: "Delete",
      icon: <RiDeleteBinLine size={14} className="text-[#FF0000]" />,
      className: "text-[#FF0000] hover:text-[#FF0000] bg-[#FFF7F7] hover:bg-[#FFEBEB]",
      onClick: async (sectionId: string) => {
        // Skip actions on optimistic sections that don't have a real backend ID yet
        if (sectionId.startsWith("__temp-")) return;
        if (
          selectedAccount?.id &&
          selectedCreativeReport?.id &&
          reportConfig?.sections &&
          sectionId
        ) {
          const originalSections = [...reportConfig.sections];
          try {
            const updatedSections = reportConfig.sections.filter(
              (s) => s.id !== sectionId,
            );
            if (selectedSection?.id === sectionId) {
              if (updatedSections.length > 0) {
                setSelectedSection(updatedSections[0]);
              } else {
                setSelectedSection(null);
              }
            }
            setReportConfig({
              ...reportConfig,
              sections: updatedSections,
            });
            await deleteReportSection(
              selectedAccount?.id,
              selectedCreativeReport.id,
              sectionId,
            );
          } catch (error) {
            console.error(error);
            setReportConfig({
              ...reportConfig,
              sections: originalSections,
            });
          }
        }
      },
      disabled: reportConfig?.sections.length === 1,
    },
  ];

  // Update the delete handler
  const handleDelete = async () => {
    if (!selectedAccount?.id || !reportToDelete.current?.id) return;
    const originalReports = [...creativeReportList];
    const originalSelectedReport = selectedCreativeReport;
    try {
      const newList = creativeReportList.filter(
        (r) => r.id !== reportToDelete.current?.id,
      );
      setCreativeReportList(newList);
      if (selectedCreativeReport?.id === reportToDelete.current.id) {
        const firstRegularReport =
          newList.find((r) => r.type === ReportType.Regular) ?? null;
        handleReportChange(firstRegularReport);
      }
      await deleteCreativeReport(selectedAccount?.id, reportToDelete.current.id);
      toast.message(
        <ToastContent
          headingPrefix="Success!"
          subheadingSuffix="Creative report deleted successfully."
        />,
      );
    } catch (error) {
      console.error(error);
      setCreativeReportList(originalReports);
      handleReportChange(originalSelectedReport);
    } finally {
      reportToDelete.current = null;
    }
  };

  return (
    <>
      <AlertDialog open={openDeleteAlert} onOpenChange={setOpenDeleteAlert}>
        <AlertDialogContent overlayClassName="bg-black/50" className="!rounded-[12px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#344054]">
              Are you absolutely sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              Confirm deletion of the{" "}
              <span className="text-[#344054] font-medium">
                '{reportToDelete.current?.title}'
              </span>{" "}
              report. This action will permanently remove the report and all its data. It
              cannot be undone and all related information will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(e) => e.stopPropagation()}
              className="text-brand-primary-new rounded-[8px] px-[14px] py-[6px]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                void handleDelete();
                setOpenDeleteAlert(false);
              }}
              className="bg-[#D92D20] hover:bg-[#D92D20]/90 rounded-[8px] border-0 text-white px-[14px] py-[6px]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={isAddSectionDialogOpen} onOpenChange={setIsAddSectionDialogOpen}>
        <DialogContent
          className="max-w-none w-auto !p-0 !gap-0 !rounded-t-[16px] !rounded-b-none border-none !bg-transparent !shadow-none !top-[48px] !translate-y-0 h-[calc(100vh-48px)] flex flex-col overflow-hidden"
          hideCloseButton
        >
          <DialogTitle className="sr-only">Add Section</DialogTitle>
          <DialogDescription className="sr-only">
            Choose a template to create a new section
          </DialogDescription>
          <div className="preset-slide-panel flex flex-col flex-1 min-h-0 pt-[12px] px-[12px] bg-white rounded-t-[16px]">
            <SectionPreset
              onSelect={(item) => {
                setIsAddSectionDialogOpen(false);
                if (item.isCustom) {
                  setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set("type", "label");
                    params.set("apply", "true");
                    params.set("customPreset", "true");
                    return params;
                  });
                  return;
                }
                void handleAddSection(item.configuration, item.title, item);
              }}
              onSkip={() => {
                setIsAddSectionDialogOpen(false);
                void handleAddSection();
              }}
              availableGroupByKeys={availableGroupByKeys}
              platform={selectedAccount?.platform}
            />
          </div>
          <style>{`
            .preset-slide-panel {
              animation: preset-slide-up 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
            }
            @keyframes preset-slide-up {
              from {
                opacity: 0;
                transform: translateY(60px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </DialogContent>
      </Dialog>
      <SidebarWrapper>
        <div className="w-full h-full overflow-hidden flex flex-col justify-between gap-[10px]">
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex flex-col gap-1 w-full h-[141px]">
              {Object.values(creativeSidebarItems).map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  isSelected={selectedSidebarItem?.id === item.id}
                  onSelect={() => handleSidebarItemClick(item)}
                />
              ))}
            </div>
            {selectedSidebarItem?.id === "regular" && (
              <>
                <SidebarSeparator />
                <div
                  className={`flex items-center ${isReportSearching ? "pl-0 pr-3" : "px-3"} mb-1 gap-2 h-7`}
                >
                  <div className="flex-1 flex justify-between items-center gap-2">
                    {!isReportSearching ? (
                      <>
                        <div className="text-brand-primary-400 text-[12px]">Reports</div>
                        <button
                          className="bg-inherit rounded-full"
                          onClick={() => setIsReportSearching(true)}
                        >
                          <RiSearchLine
                            size={14}
                            className="flex-shrink-0 text-brand-primary-400"
                          />
                        </button>
                      </>
                    ) : (
                      <div className="w-full flex gap-[14px] items-center bg-selected-gray/80 rounded-[8px] p-2 h-7">
                        <input
                          ref={reportSearchInputRef}
                          type="text"
                          value={reportSearchQuery}
                          onChange={(e) => setReportSearchQuery(e.target.value)}
                          className="bg-inherit w-full outline-none border-none overflow-hidden text-brand-primary-new placeholder:text-brand-primary-400 text-ellipsis text-[12px] font-normal tracking-[0.28px]"
                          placeholder="Search reports"
                        />
                        <button
                          onClick={() => {
                            setIsReportSearching(false);
                            setReportSearchQuery("");
                          }}
                          className="bg-inherit rounded-full"
                        >
                          <IconX size={12} className="shrink-0 text-brand-primary-400" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center rounded-[4px]">
                    <Button
                      className="p-0 border-none shadow-none hover:bg-inherit bg-inherit rounded-full"
                      onClick={() => void handleAddReport()}
                      disabled={
                        isCreatingReport || !dataConfigurations || isCreativeListLoading
                      }
                    >
                      {isCreatingReport ? (
                        <IconLoader2
                          size={14}
                          className="shrink-0 animate-spin text-brand-primary-400"
                        />
                      ) : (
                        <RiAddLargeLine
                          size={14}
                          className="shrink-0 text-brand-primary-400"
                        />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex-1 w-full overflow-y-auto">
                  <div className="flex flex-col gap-[2px]">
                    {isCreativeListLoading ? (
                      <SidebarLoader />
                    ) : (
                      <>
                        <Reorder.Group
                          axis="y"
                          values={reportsOrder}
                          onReorder={handleReportsReorder}
                          className="flex flex-col gap-[2px]"
                        >
                          {reportsOrder.map((report) => (
                            <Reorder.Item
                              key={report.id}
                              value={report}
                              className="w-full rounded-[4px] cursor-pointer overflow-hidden"
                              onDragStart={() => {
                                setIsDraggingReports(true);
                                setDraggingReportId(report.id || null);
                              }}
                              onDragEnd={(e: MouseEvent | TouchEvent | PointerEvent) => {
                                e.preventDefault();
                                handleReportsDragEnd();
                              }}
                              transition={{
                                layout: isDraggingReports
                                  ? { type: "spring", stiffness: 400, damping: 40 }
                                  : { duration: 0 },
                                x: { type: "spring", stiffness: 300, damping: 30 },
                                y: { type: "spring", stiffness: 300, damping: 30 },
                              }}
                            >
                              <SidebarSubItem
                                item={convertReportToSidebarItem(report)}
                                isSelected={selectedCreativeReport?.id === report.id}
                                selectedChildId={selectedSection?.id || null}
                                isBeingDragged={draggingReportId === report.id}
                                onSelect={(item) => {
                                  // Prevent selection if we're currently dragging
                                  if (isDraggingReports) return;

                                  // If the item has children, it's a report
                                  if (item.children) {
                                    const report = creativeReportList.find(
                                      (r) => r.id === item.id,
                                    );
                                    if (report) {
                                      handleReportChange(report);
                                    }
                                  } else {
                                    // If no children, it's a section
                                    const section = reportConfig?.sections.find(
                                      (s) => s.id === item.id,
                                    );
                                    if (section) {
                                      setSelectedSection(section);
                                    }
                                  }
                                }}
                                parentActions={handleReportActions}
                                childActions={handleSectionActions}
                                onAddChild={() => setIsAddSectionDialogOpen(true)}
                                hideAddChildButton={hideAddSectionButton}
                                isAddingChild={isAddingSection}
                                isParentDraggable={true}
                                isChildDraggable={true}
                                children={
                                  selectedCreativeReport?.id === report.id && reportConfig
                                    ? reportConfig.sections.map((section) => ({
                                        id: section.id || null,
                                        type: "tab" as const,
                                        value: section.id || "",
                                        label: section.title,
                                        icon: (
                                          <span className="mt-1">{section.icon}</span>
                                        ),
                                      }))
                                    : undefined
                                }
                                setChildren={(children) => {
                                  // Call the API to commit child reordering changes
                                  void handleChildReorder(children);
                                }}
                                isChildLoading={
                                  selectedCreativeReport?.id === report.id &&
                                  reportConfigLoading
                                }
                              />
                            </Reorder.Item>
                          ))}
                        </Reorder.Group>

                        {/* Show skeleton loaders for pending reports */}
                        {pendingReports.map((report) => (
                          <div key={`pending-${report.id}`} className="flex w-full">
                            <Skeleton className="w-full h-[28px] rounded-[8px] bg-gradient-to-r from-[#FEF4F1] via-[#FDEFF5] to-[#FDEFF5]" />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          {accountSyncStatus && (
            <SyncedAdsStatus
              syncStatus={accountSyncStatus}
              onRetry={() => void handleRetrySync()}
              isRetrying={isRetryingSync}
            />
          )}
        </div>
      </SidebarWrapper>
    </>
  );
});

export default CreativesSidebar;
